/** Read-only view of one rate card, with the manager actions beside it. Archiving asks first, in place. */

import React, { useState } from 'react';
import { Button, Card, StatusPill, tableStyles } from '../../ui';
import InlineConfirm from '../MediaPlans/InlineConfirm';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, MediaPlanRateCard, MediaPlanUnitDef, RateCardStatus } from '../MediaPlans/types';
import { CARD_STATUS_LABELS, LAYER_LABELS, SCOPE_LABELS } from './rateCardDraft';
import { ListStatusPill, STATUS_VARIANT, cardTargetLabel } from './RateCardList';
import { fmtDate, fmtUsd, mutedText, sectionCardStyle, sectionTitleStyle } from '../MediaPlans/formStyles';

interface Props {
  card: MediaPlanRateCard;
  units: MediaPlanUnitDef[];
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  advertisers: AdvertiserRecord[];
  canManage: boolean;
  busy: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onSetStatus: (status: RateCardStatus) => void;
  onClose: () => void;
}

const RateCardPanel: React.FC<Props> = ({ card, units, categories, agencies, advertisers, canManage, busy, onEdit, onDuplicate, onSetStatus, onClose }) => {
  const [confirmArchive, setConfirmArchive] = useState(false);
  const unitLabel = (k: string) => units.find((u) => u.key === k)?.shortLabel || k;
  return (
    <Card style={sectionCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--fg-1)' }}>{card.name}</h3>
            <StatusPill variant={STATUS_VARIANT[card.status]} size="sm">
              {CARD_STATUS_LABELS[card.status]}
            </StatusPill>
            <ListStatusPill card={card} />
          </div>
          <div style={mutedText}>
            {SCOPE_LABELS[card.scope]} card · {cardTargetLabel(card, categories, agencies, advertisers)} · effective {fmtDate(card.effectiveFrom)}
            {card.effectiveTo ? ` – ${fmtDate(card.effectiveTo)}` : ' onward'} · v{card.version} · {card.id}
            {card.pricingMode === 'all-in' ? ' · all-in rates (targeting included)' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {canManage && (
            <>
              <Button size="sm" variant="primary" onClick={onEdit} disabled={busy}>
                Edit
              </Button>
              <Button size="sm" variant="subtle" onClick={onDuplicate} disabled={busy}>
                Duplicate
              </Button>
              {card.status !== 'active' && (
                <Button size="sm" variant="subtle" onClick={() => onSetStatus('active')} disabled={busy}>
                  Activate
                </Button>
              )}
              {card.status === 'active' && card.scope !== 'default' && (
                <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(true)} disabled={busy || confirmArchive}>
                  Archive
                </Button>
              )}
            </>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {confirmArchive && (
        <InlineConfirm
          title={`Archive “${card.name}”?`}
          confirmLabel="Archive"
          busyLabel="Archiving…"
          tone="danger"
          busy={busy}
          onConfirm={() => {
            setConfirmArchive(false);
            onSetStatus('archived');
          }}
          onCancel={() => setConfirmArchive(false)}
        >
          Plans already saved keep their prices; new quotes stop using this card.
        </InlineConfirm>
      )}

      <div>
        <div style={sectionTitleStyle}>Unit prices</div>
        <div style={{ ...tableStyles.wrapScroll, marginTop: 8 }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Ad unit</th>
                <th style={tableStyles.thNum}>CPM</th>
                <th style={tableStyles.thNum}>Flat</th>
                <th style={tableStyles.thNum}>Floor</th>
                <th style={tableStyles.th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => {
                const p = card.units[u.key];
                return (
                  <tr key={u.key}>
                    <td style={tableStyles.td}>{u.label}</td>
                    <td style={tableStyles.tdNum}>{typeof p?.cpm === 'number' ? fmtUsd(p.cpm) : <span style={mutedText}>—</span>}</td>
                    <td style={tableStyles.tdNum}>{typeof p?.flatUsd === 'number' ? `${fmtUsd(p.flatUsd, 0)}${p.flatPer ? ` / ${p.flatPer}` : ''}` : <span style={mutedText}>—</span>}</td>
                    <td style={tableStyles.tdNum}>{typeof p?.floorCpm === 'number' ? fmtUsd(p.floorCpm) : <span style={mutedText}>—</span>}</td>
                    <td style={{ ...tableStyles.td, ...mutedText }}>{p?.note || (p ? '' : 'not priced — falls through to the next card')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <div>
          <div style={sectionTitleStyle}>Targeting upcharges</div>
          {card.upcharges.length === 0 ? (
            <div style={{ ...mutedText, marginTop: 6 }}>None on this card (inherits from the next card down).</div>
          ) : (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--fg-2)', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {card.upcharges.map((u, i) => (
                <li key={`${u.layer}-${i}`}>
                  <strong>{u.label}</strong> · {LAYER_LABELS[u.layer]} · +{fmtUsd(u.cpmAdd)} CPM
                  {u.appliesTo?.length ? ` · ${u.appliesTo.map(unitLabel).join(', ')}` : ''}
                  {u.freeWhen === 'age-only' ? ' · waived for age-only demos' : ''}
                  {u.note ? <span style={mutedText}> — {u.note}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div style={sectionTitleStyle}>Added value</div>
          {card.addedValue ? (
            <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 6 }}>
              {Math.round(card.addedValue.bonusPct * 100)}% bonus impressions on {card.addedValue.bonusUnits.map(unitLabel).join(', ')}
              {card.addedValue.customItems?.length ? (
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {card.addedValue.customItems.map((c) => (
                    <li key={c.label}>
                      {c.label} at {fmtUsd(c.minBudgetUsd, 0)}+{typeof c.valueUsd === 'number' ? ` (${fmtUsd(c.valueUsd, 0)} value)` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              {card.addedValue.notes && <div style={mutedText}>{card.addedValue.notes}</div>}
            </div>
          ) : (
            <div style={{ ...mutedText, marginTop: 6 }}>No policy on this card (inherits).</div>
          )}
        </div>
        <div>
          <div style={sectionTitleStyle}>Default unit mix</div>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 6 }}>
            {card.defaultUnitMix && Object.keys(card.defaultUnitMix).length
              ? Object.entries(card.defaultUnitMix).map(([k, v]) => `${unitLabel(k)} ${v}%`).join(' · ')
              : <span style={mutedText}>None (inherits).</span>}
          </div>
          {typeof card.minimums?.budgetUsd === 'number' && <div style={{ ...mutedText, marginTop: 6 }}>Minimum budget {fmtUsd(card.minimums.budgetUsd, 0)}</div>}
        </div>
      </div>

      {card.notes && <div style={{ ...mutedText, whiteSpace: 'pre-wrap' }}>{card.notes}</div>}
      <div style={mutedText}>
        Created {fmtDate(card.createdAt)} by {card.createdBy?.email} · updated {fmtDate(card.updatedAt)} by {card.updatedBy?.email}
      </div>
    </Card>
  );
};

export default RateCardPanel;
